import React, { memo, useCallback, useEffect, useState, useMemo } from 'react';
import { BASE_URL, buildImageUrl } from '../helper/constants';
import VideoPlayers from './VideoPlayers';
import ImagesWithVideos from './ImagesWithVideos';
import PdfPreviewTile from './PdfPreviewTile';
import PdfViewerModal from './PdfViewerModal';
import ChatVideoBubble from './chatMedia/ChatVideoBubble';
import VideoPreviewThumb from './chatMedia/VideoPreviewThumb';
import { isNativeVideoAttachment, buildVideoSrc, buildVideoPosterUrl, getVideoMimeType } from '../utils/chatVideo';
import './PlayerFeed.css';

function resolveComposerSrc(item) {
    if (!item) return '';
    if (item.url && (String(item.url).startsWith('blob:') || String(item.url).startsWith('data:'))) {
        return item.url;
    }
    const local = !!(item.local || (item.url && String(item.url).startsWith('blob:')));
    return buildImageUrl(local, item.fileUrl || item.href || item.url, item.attachedFileName || item.originalName || item.name);
}

function isPdfItem(image) {
    if (!image) return false;
    if (image.isPdf || image.type === 'application/pdf') return true;
    if (image.fileType === 'doc' || image.fileType === 'application/pdf') {
        const nameHint = image.attachedFileName || image.originalName || image.name || image.fileUrl || image.href || '';
        if (/\.pdf(\?|$)/i.test(String(nameHint))) return true;
    }
    return (image.name && /\.pdf$/i.test(image.name))
        || (image.attachedFileName && /\.pdf$/i.test(image.attachedFileName))
        || (image.fileUrl && /\.pdf(\?|$)/i.test(image.fileUrl))
        || (image.href && /\.pdf(\?|$)/i.test(image.href));
}

function resolvePdfUrl(item) {
    if (!item) return '';
    if (item.url && (item.url.startsWith('blob:') || item.url.startsWith('http'))) return item.url;
    if (item.local && item.url) return item.url;
    if (item.href) {
        if (item.href.startsWith('http')) return item.href;
        return BASE_URL + item.href;
    }
    if (item.fileUrl) {
        const furl = Array.isArray(item.fileUrl) ? item.fileUrl[0] : item.fileUrl;
        if (furl && (furl.startsWith('http') || furl.startsWith('blob:'))) return furl;
        if (furl) return BASE_URL + furl;
    }
    return '';
}

function getPdfName(item) {
    if (!item) return 'document.pdf';
    if (item.attachedFileName) return String(item.attachedFileName).split('?')[0];
    if (item.originalName) return String(item.originalName).split('?')[0];
    if (item.fileName && !/^[0-9a-f-]{30,}\.pdf$/i.test(item.fileName)) return String(item.fileName).split('?')[0];
    if (item.name && !/^[0-9a-f-]{30,}\.pdf$/i.test(item.name)) return String(item.name).split('?')[0];
    return 'document.pdf';
}

const ARTICLE_VIDEO_SIZE_KEY = 'kjuup-article-video-sizes';
const articleVideoSizeMem = new Map();
const FEED_VIDEO_SCALE = 0.85;

function videoSizeCacheKey(src, poster, fileUrl) {
    return src || poster || fileUrl || '';
}

function readCachedVideoSize(key) {
    if (!key) return null;
    if (articleVideoSizeMem.has(key)) return articleVideoSizeMem.get(key);
    try {
        const all = JSON.parse(sessionStorage.getItem(ARTICLE_VIDEO_SIZE_KEY) || '{}');
        if (all[key] && all[key].w > 0 && all[key].h > 0) {
            articleVideoSizeMem.set(key, all[key]);
            return all[key];
        }
    } catch (_) { /* ignore */ }
    return null;
}

function writeCachedVideoSize(key, size) {
    if (!key || !size?.w || !size?.h) return;
    articleVideoSizeMem.set(key, size);
    try {
        const all = JSON.parse(sessionStorage.getItem(ARTICLE_VIDEO_SIZE_KEY) || '{}');
        all[key] = size;
        sessionStorage.setItem(ARTICLE_VIDEO_SIZE_KEY, JSON.stringify(all));
    } catch (_) { /* ignore */ }
}

function peekPosterSize(posterUrl) {
    if (!posterUrl || typeof Image === 'undefined') return null;
    const img = new Image();
    img.src = posterUrl;
    if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
        return { w: img.naturalWidth, h: img.naturalHeight };
    }
    return null;
}

function itemStoredSize(item) {
    const w = Number(item?.width || item?.videoWidth || 0);
    const h = Number(item?.height || item?.videoHeight || 0);
    if (w > 0 && h > 0) return { w, h };
    return null;
}

function ArticlePreviewNativeVideo({ item, compact }) {
    const blobUrl = item?.url && String(item.url).startsWith('blob:') ? item.url : '';
    const local = !!(item?.local || blobUrl);
    const src = buildVideoSrc(local, item?.fileUrl || item?.url, item?.attachedFileName || item?.name) || blobUrl;
    const poster = buildVideoPosterUrl(local, item?.thumbnailUrl, item?.fileUrl || item?.url);
    const cacheKey = videoSizeCacheKey(src, poster, item?.fileUrl);

    const [size, setSize] = useState(() => (
        itemStoredSize(item)
        || readCachedVideoSize(cacheKey)
        || peekPosterSize(poster)
    ));

    const applySize = useCallback((w, h) => {
        const nextW = Math.round(Number(w) || 0);
        const nextH = Math.round(Number(h) || 0);
        if (nextW <= 0 || nextH <= 0) return;
        setSize((prev) => {
            if (prev && prev.w === nextW && prev.h === nextH) return prev;
            const next = { w: nextW, h: nextH };
            writeCachedVideoSize(cacheKey, next);
            return next;
        });
    }, [cacheKey]);

    useEffect(() => {
        if (size || !poster) return undefined;
        const img = new Image();
        img.onload = () => applySize(img.naturalWidth, img.naturalHeight);
        img.src = poster;
        return () => { img.onload = null; };
    }, [poster, size, applySize]);

    const ready = !!(size && size.w > 0 && size.h > 0);
    const scale = compact ? FEED_VIDEO_SCALE : 1;
    const displayW = ready ? Math.round(size.w * scale) : undefined;

    return (
        <div
            className={`article-preview-native-video${compact ? ' is-feed' : ''}${ready ? ` is-ready player-feed__native-video${compact ? ' player-feed__native-video--feed' : ''}` : ''}`}
            style={ready ? {
                '--player-feed-video-w': `${displayW}px`,
                '--player-feed-video-ar': `${size.w} / ${size.h}`,
            } : undefined}
        >
            <video
                controls
                playsInline
                preload="metadata"
                poster={poster || undefined}
                src={src}
                width={ready ? size.w : undefined}
                height={ready ? size.h : undefined}
                onLoadedMetadata={(e) => applySize(e.currentTarget.videoWidth, e.currentTarget.videoHeight)}
            >
                <source src={src} type={getVideoMimeType(item?.fileUrl || item?.url || item?.name)} />
            </video>
        </div>
    );
}

function ComposerNativeVideoTile({ image, onRemove, showRemove }) {
    const blobUrl = image?.url && String(image.url).startsWith('blob:') ? image.url : '';
    const local = !!(image?.local || blobUrl);
    const busy = !!(image?.processing || image?.uploading);
    return (
        <div
            className={`article-composer-image article-composer-native-video player-feed__video-tile${busy ? ' is-busy' : ''}`}
        >
            {showRemove && (
                <span
                    className="material-symbols-outlined pointer position-absolute remove-image-icon player-feed__remove"
                    onClick={onRemove}
                    title="Remove"
                >
                    cancel
                </span>
            )}
            {busy ? (
                <ChatVideoBubble
                    fileUrl={image.fileUrl || image.url}
                    thumbnailUrl={image.thumbnailUrl || blobUrl || image.url}
                    local={local}
                    attachedFileName={image.attachedFileName || image.originalName || image.name}
                    uploading={busy}
                    progress={image.progress}
                    processing={!!image.processing}
                    hideToolbar
                />
            ) : (
                <VideoPreviewThumb item={image} playSize={40} />
            )}
        </div>
    );
}

const PlayerFeed = ({ Links, article, isC, removeImage, onPress, prev, viewArticleDetail, isInternal = false }) => {
    const [_LNK, setLNK] = useState([])
    const [pdfViewer, setPdfViewer] = useState(null);

    useEffect(() => {
        setLNK(Array.isArray(Links) ? [...Links] : [])
    }, [Links])

    const { imageLinks, docLinks, nativeVideos, galleryLinks, mixedGallery } = useMemo(() => {
        const images = [];
        const docs = [];
        const videos = [];
        const gallery = [];
        _LNK.forEach((item) => {
            if (isPdfItem(item)) {
                docs.push(item);
            } else {
                gallery.push(item);
                if (isNativeVideoAttachment(item) && !item.videoType) {
                    videos.push(item);
                } else {
                    images.push(item);
                }
            }
        });
        return {
            imageLinks: images,
            docLinks: docs,
            nativeVideos: videos,
            galleryLinks: gallery,
            mixedGallery: images.length > 0 && videos.length > 0,
        };
    }, [_LNK]);

    if (_LNK && isC && !prev) {
        return (
            <div className="article-img">
                {(mixedGallery ? galleryLinks : imageLinks).length > 0 && (
                    <div className="article-composer-images">
                        {(mixedGallery ? galleryLinks : imageLinks).map((image) => {
                            const originalIndex = _LNK.indexOf(image);
                            const isNative = isNativeVideoAttachment(image) && !image.videoType;
                            if (isNative) {
                                return (
                                    <ComposerNativeVideoTile
                                        key={image.clientId || image.fileUrl || originalIndex}
                                        image={image}
                                        showRemove={isC}
                                        onRemove={() => removeImage(originalIndex)}
                                    />
                                );
                            }
                            const src = resolveComposerSrc(image);
                            return (
                                <div
                                    key={image.clientId || image.fileUrl || originalIndex}
                                    className={image.videoType ? "article-composer-embed" : "article-composer-image player-feed__image-tile"}
                                >
                                    {image.videoType ? (
                                        <VideoPlayers
                                            url={image.href ? image.href : image.fileUrl}
                                            type={image.videoType}
                                            isPlaying={image.play}
                                            fileType={image.fileType}
                                            local={image.url}
                                            article={article}
                                            isC={isC}
                                            index={originalIndex}
                                            removeImage={(e) => removeImage(e)}
                                            viewArticle={viewArticleDetail}
                                            fileName={image?.attachedFileName || image?.originalName || image?.name || ''}
                                            thumbnailUrl={image?.thumbnailUrl}
                                        />
                                    ) : (
                                        <>
                                            {isC && (
                                                <span
                                                    className="material-symbols-outlined pointer position-absolute remove-image-icon player-feed__remove player-feed__remove--image"
                                                    onClick={() => removeImage(originalIndex)}
                                                    title="Remove"
                                                >
                                                    cancel
                                                </span>
                                            )}
                                            <img
                                                className="pointer player-feed__image"
                                                src={src}
                                                alt=""
                                            />
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                {!mixedGallery && nativeVideos.length > 0 && (
                    <div className="article-composer-videos">
                        {nativeVideos.map((image) => {
                            const originalIndex = _LNK.indexOf(image);
                            const blobUrl = image.url && String(image.url).startsWith('blob:') ? image.url : '';
                            const local = !!(image.local || blobUrl);
                            const busy = !!(image.processing || image.uploading);
                            return (
                                <div
                                    className="article-composer-video"
                                    key={image.clientId || originalIndex}
                                >
                                    {isC && (
                                        <span
                                            className="material-symbols-outlined pointer position-absolute remove-image-icon player-feed__remove player-feed__remove--video"
                                            onClick={() => removeImage(originalIndex)}
                                            title="Remove"
                                        >
                                            cancel
                                        </span>
                                    )}
                                    {busy ? (
                                        <ChatVideoBubble
                                            fileUrl={image.fileUrl || image.url}
                                            thumbnailUrl={image.thumbnailUrl || blobUrl || image.url}
                                            local={local}
                                            attachedFileName={image.attachedFileName || image.originalName || image.name}
                                            uploading={busy}
                                            progress={image.progress}
                                            processing={!!image.processing}
                                            hideToolbar
                                        />
                                    ) : (
                                        <VideoPreviewThumb item={image} playSize={40} />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                {docLinks.length > 0 && (
                    <div className={`player-feed__doc-grid${docLinks.length === 1 ? ' player-feed__doc-grid--single' : ''}${imageLinks.length > 0 ? ' player-feed__doc-grid--spaced' : ''}`}>
                        {docLinks.map((doc) => {
                            const originalIndex = _LNK.indexOf(doc);
                            const pdfUrl = resolvePdfUrl(doc);
                            const pdfName = getPdfName(doc);
                            return (
                                <div key={originalIndex}>
                                    <PdfPreviewTile
                                        pdfUrl={pdfUrl}
                                        fileName={pdfName}
                                        variant="compact"
                                        onClick={() => { if (pdfUrl) setPdfViewer({ url: pdfUrl, name: pdfName }); }}
                                        onRemove={() => removeImage(originalIndex)}
                                        showRemove={true}
                                        isInternal={isInternal}
                                        maxHeight={docLinks.length === 1 ? 460 : 280}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
                {pdfViewer && (
                    <PdfViewerModal
                        url={pdfViewer.url}
                        fileName={pdfViewer.name}
                        onClose={() => setPdfViewer(null)}
                    />
                )}
            </div>
        );
    }

    return (
        <div className="article-img">
            {(mixedGallery ? galleryLinks : imageLinks).length > 0 && (
                <ImagesWithVideos
                    isC={isC}
                    data={mixedGallery ? galleryLinks : imageLinks}
                    onPress={onPress ? (data, index) => onPress(data, index) : undefined}
                    isInternal={isInternal}
                />
            )}
            {!mixedGallery && nativeVideos.length > 0 && (
                <div
                    className="article-preview-native-videos player-feed__native-videos"
                >
                    {nativeVideos.map((image, index) => (
                        <ArticlePreviewNativeVideo
                            key={image.clientId || image.fileUrl || image.url || index}
                            item={image}
                            compact={!prev}
                        />
                    ))}
                </div>
            )}
            {docLinks.length > 0 && (
                <div className={`player-feed__doc-list${imageLinks.length > 0 ? ' player-feed__doc-list--spaced' : ''}`}>
                    {docLinks.map((doc, index) => {
                        const pdfUrl = resolvePdfUrl(doc);
                        const pdfName = getPdfName(doc);
                        return (
                            <PdfPreviewTile
                                key={index}
                                item={doc}
                                pdfUrl={pdfUrl}
                                fileName={pdfName}
                                variant="card"
                                onClick={() => { if (pdfUrl) setPdfViewer({ url: pdfUrl, name: pdfName }); }}
                                isInternal={isInternal}
                                maxWidth={9999}
                                maxHeight={500}
                            />
                        );
                    })}
                </div>
            )}
            {pdfViewer && (
                <PdfViewerModal
                    url={pdfViewer.url}
                    fileName={pdfViewer.name}
                    onClose={() => setPdfViewer(null)}
                />
            )}
        </div>
    );
}
export default memo(PlayerFeed);
